'use client';

import {
  createContext,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface IProductSearchBoxCtx {
  open(): void;
  close(): void;
  focus(): void;
  reset(): void;
  modal: MutableRefObject<null>;
  dialog: MutableRefObject<HTMLDialogElement | null>;
  available: boolean;
  resetBtn: MutableRefObject<null>;
  setAvailable: Dispatch<SetStateAction<boolean>>;
  loaded: boolean;
}

export const ProductSearchBoxContext =
  createContext<IProductSearchBoxCtx | null>(null);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ProductSearchBoxProvider({ children }: { children: any }) {
  const [available, setAvailable] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const modal = useRef(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const resetBtn = useRef(null);

  const focus = useCallback(function focus() {
    if (!(dialog?.current as unknown as HTMLDialogElement)?.open) return;

    const { current } = modal;

    if (!current) return;

    const input = (current as HTMLFormElement).querySelector('input');

    input?.focus();
  }, []);

  const reset = useCallback(function reset() {
    if (!(dialog?.current as unknown as HTMLDialogElement)?.open) return;

    const { current } = modal;

    if (!current) return;

    (current as HTMLFormElement).reset();
  }, []);

  const open = useCallback(function open() {
    setLoaded(true);
    const { current } = dialog;

    if (!current) return;

    if ((current as HTMLDialogElement).open) return;

    (current as HTMLDialogElement).showModal();
  }, []);

  const close = useCallback(function close() {
    const { current } = dialog;

    if (!current) return;

    if (!(current as HTMLDialogElement).open) return;

    (current as HTMLDialogElement).close();
  }, []);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        open();
      }
    };

    document.addEventListener('keydown', handleKeydown);

    return () => document.removeEventListener('keydown', handleKeydown);
  }, [open]);

  // Memoized: consumers key mount-time effects (showModal) on this object, so a
  // new identity on every render would re-open a dismissed dialog.
  const value: IProductSearchBoxCtx = useMemo(
    () => ({
      open,
      close,
      focus,
      reset,
      modal,
      dialog,
      resetBtn,
      available,
      setAvailable,
      loaded,
    }),
    [open, close, focus, reset, available, loaded]
  );

  return (
    <ProductSearchBoxContext.Provider value={value}>
      {children}
    </ProductSearchBoxContext.Provider>
  );
}
